// src/index.js
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import './i18next';
import * as Sentry from '@sentry/react';
import { App } from './App';
import { store } from './store';
import { API_ROOT as BUILD_TIME_API_ROOT, SENTRY_DSN, SENTRY_ENV } from './config';
import { initializeGuestSession } from './helpers/initializeGuestSession';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ConfigProvider } from './contexts/ConfigContext';
import { loadRuntimeConfig } from './services/configService';

import './assets/styles/index.scss';

const startApp = async () => {
  try {
    // Load runtime config first
    const runtimeConfig = await loadRuntimeConfig();
    const apiRoot = runtimeConfig.apiRoot || BUILD_TIME_API_ROOT;

    // Initialize Sentry with runtime API_ROOT
    Sentry.init({
      environment: SENTRY_ENV,
      dsn: SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: ['localhost', apiRoot],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });

    // Pass store to initializeGuestSession for RTK Query
    await initializeGuestSession(store);
    
    const root = createRoot(document.getElementById('root'));
    root.render(
      <ErrorBoundary>
        <ConfigProvider>
          <Provider store={store}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </Provider>
        </ConfigProvider>
      </ErrorBoundary>
    );
  } catch (error) {
    console.error('Failed to init app:', error);
    // If initialization fails, still try to render app with error boundary
    const root = createRoot(document.getElementById('root'));
    root.render(
      <ErrorBoundary>
        <ConfigProvider>
          <Provider store={store}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </Provider>
        </ConfigProvider>
      </ErrorBoundary>
    );
  }
};

startApp();

// Performance monitoring can be enabled by calling reportWebVitals with a callback
// Example: reportWebVitals(console.log) or reportWebVitals(sendToAnalytics)
// Currently disabled to reduce bundle size
// reportWebVitals();
